import { useState, useCallback } from 'react';
import { toast } from '@crabs-ai/ds-react';
import { useTranslations } from 'next-intl';
import { useQueryClient } from '@tanstack/react-query';
import { QueryKeyGetGtsVideos } from '@/features/gts/api/get-gts-videos';
import {
  GtsMovementStyle,
  GtsMovementStyleValue,
  GtsGenerationModeValue,
  GtsProductCategory,
  GtsVideoModel,
  GtsVideoModelValue,
  Resolution,
  AspectRatio,
  AspectRatioValue,
  VideoQuality,
} from '@/features/gts/model/gts-types';
import { usePostProjectVideoGenerate } from './usePostProjectVideoGenerate';

function getImageAspectRatio(url: string): Promise<AspectRatioValue> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      if (img.width > img.height) {
        resolve(AspectRatio.SIXTEEN_TO_NINE);
      } else if (img.width < img.height) {
        resolve(AspectRatio.NINE_TO_SIXTEEN);
      } else {
        resolve(AspectRatio.ONE_TO_ONE);
      }
    };
    img.onerror = () => {
      resolve(AspectRatio.NINE_TO_SIXTEEN);
    };
    img.src = url;
  });
}

/** 모델별 기본 영상 길이(초) */
const VIDEO_MODEL_LENGTH: Partial<Record<GtsVideoModelValue, number>> = {
  [GtsVideoModel.SORA]: 12,
  [GtsVideoModel.VEO_2]: 8,
  [GtsVideoModel.VEO_3]: 8,
  [GtsVideoModel.KLING_TURBO]: 5,
  [GtsVideoModel.SEEDANCE_1_5_PRO]: 12,
};

interface UseAiVideoGenerationOptions {
  projectId: number;
  imageUrl: string;
  videoModel?: GtsVideoModelValue;
  onSuccess?: () => void;
  uploadPendingFile?: () => Promise<string | null>;
}

export function useAiVideoGeneration({
  projectId,
  imageUrl,
  videoModel = GtsVideoModel.KLING_TURBO,
  onSuccess,
  uploadPendingFile,
}: UseAiVideoGenerationOptions) {
  const t = useTranslations();
  const queryClient = useQueryClient();
  const [movementStyle, setMovementStyle] = useState<GtsMovementStyleValue>(
    GtsMovementStyle.NATURAL,
  );
  const [customPrompt, setCustomPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  const { mutate: generateVideo } = usePostProjectVideoGenerate(projectId);

  const handleGenerate = useCallback(async () => {
    let finalImageUrl = imageUrl;

    // pendingFile이 있으면 먼저 업로드
    if (uploadPendingFile) {
      setIsGenerating(true);
      try {
        const uploadedUrl = await uploadPendingFile();
        if (!uploadedUrl) {
          toast({
            type: 'error',
            description: t('Editor.ai-video.upload-fail'),
            toastOptions: { position: 'top-center' },
          });
          setIsGenerating(false);
          return;
        }
        finalImageUrl = uploadedUrl;
      } catch {
        toast({
          type: 'error',
          description: t('Editor.ai-video.upload-fail'),
          toastOptions: { position: 'top-center' },
        });
        setIsGenerating(false);
        return;
      }
    }

    if (!finalImageUrl) return;

    setIsGenerating(true);

    const aspectRatio = await getImageAspectRatio(finalImageUrl);

    generateVideo(
      {
        videoProvider: videoModel,
        modelSettings: {
          videoProvider: videoModel,
          resolution: Resolution.HD,
          aspectRatio,
          quality: VideoQuality.STANDARD,
          length: VIDEO_MODEL_LENGTH[videoModel] ?? 5,
        },
        referenceImageUrl: finalImageUrl,
        mode: GtsGenerationModeValue.SHOWCASE,
        movementStyle: movementStyle,
        productCategory: GtsProductCategory.GENERAL,
        customPrompt: customPrompt || undefined,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: [QueryKeyGetGtsVideos, projectId],
          });
          toast({
            type: 'success',
            description: t('Editor.ai-video.generate-success'),
            toastOptions: { position: 'top-center' },
          });
          setIsGenerating(false);
          onSuccess?.();
        },
        onError: () => {
          setIsGenerating(false);
          toast({
            type: 'error',
            description: t('Editor.ai-video.generate-fail'),
            toastOptions: { position: 'top-center' },
          });
        },
      },
    );
  }, [
    imageUrl,
    uploadPendingFile,
    projectId,
    videoModel,
    movementStyle,
    customPrompt,
    generateVideo,
    queryClient,
    t,
    onSuccess,
  ]);

  const reset = useCallback(() => {
    setMovementStyle(GtsMovementStyle.NATURAL);
    setCustomPrompt('');
    setIsGenerating(false);
  }, []);

  return {
    movementStyle,
    setMovementStyle,
    customPrompt,
    setCustomPrompt,
    isGenerating,
    handleGenerate,
    reset,
  };
}
